import config from '../../../../config';
import storage from '../../../../hooks/useStorage/useStorage';
import { captureErrorInApm } from '../../../../lib/apm/utils/captureErrorInApm.util';
import { AesCtrEncryption } from '../../../../lib/encryption/AesCtrEncryption';
import {
  DecryptOfflineDataParams,
  EncryptOfflineDataParams,
  RecursiveTransformParams,
} from './offlineDataEncryption.types';

export class OfflineDataEncryption {
  private readonly FINAL_ID_KEY = 'Id';
  private readonly commonFieldsToIgnore = ['id', 'status'];
  private readonly counterValue = config.offline.encryption.counterValue;
  private readonly key = storage.getOfflineEncryptionKey(storage.getTeamId());

  public encryptOfflineData<T>({
    input,
    customFieldsToIgnore = [],
  }: EncryptOfflineDataParams<T>): T {
    return this.recursiveTransform({
      value: input,
      customFieldsToIgnore,
      transformFn: (plaintext: string) =>
        AesCtrEncryption.encrypt({
          plaintext,
          key: this.key,
          counterValue: this.counterValue,
        }),
    });
  }

  public decryptOfflineData<T>({
    input,
    customFieldsToIgnore = [],
  }: DecryptOfflineDataParams<T>): T {
    return this.recursiveTransform({
      value: input,
      customFieldsToIgnore,
      transformFn: (encryptedHex: string) => {
        try {
          const decryptedText = AesCtrEncryption.decrypt({
            encryptedHex,
            key: this.key,
            counterValue: this.counterValue,
          });

          return decryptedText;
        } catch (error: any) {
          console.error('[OfflineDataEncryption] decryption error:', error, {
            encryptedHex,
            key: this.key,
            counterValue: this.counterValue,
          });

          captureErrorInApm.offlineError(error);

          return encryptedHex;
        }
      },
    });
  }

  private recursiveTransform({
    value,
    transformFn,
    customFieldsToIgnore,
  }: RecursiveTransformParams): any {
    const fieldsToIgnore = [...this.commonFieldsToIgnore, ...customFieldsToIgnore];

    if (typeof value === 'string') {
      return transformFn(value);
    }

    if (Array.isArray(value)) {
      return value.map((item) =>
        this.recursiveTransform({ value: item, transformFn, customFieldsToIgnore }),
      );
    }

    if (value !== null && typeof value === 'object' && !(value instanceof Date)) {
      return Object.keys(value).reduce(
        (acc, key) => {
          if (fieldsToIgnore.includes(key) || key.endsWith(this.FINAL_ID_KEY)) {
            acc[key] = value[key];
          } else {
            acc[key] = this.recursiveTransform({
              transformFn,
              value: value[key],
              customFieldsToIgnore,
            });
          }
          return acc;
        },
        {} as Record<string, any>,
      );
    }

    return value;
  }
}
